/**
 * ÖSD Zertifikat B2 blog post: Redemittel Schreiben & Sprechen (PDF).
 * Same structure as other posts: intro, table, sections, FAQ accordion.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import RedemittelPhraseTables from './RedemittelPhraseTables';

const BLOG_FAQ_ITEMS = [
  {
    question: 'How long is the ÖSD B2 Writing module?',
    answer: 'The written module (Schreiben) lasts 90 minutes. You must complete two tasks: a formal piece of correspondence and an argumentative text. Managing your time between these two is the biggest challenge for candidates.',
  },
  {
    question: 'Does ÖSD B2 differ between Germany, Austria, and Switzerland?',
    answer: "The exam itself is standard, but the ÖSD is unique because it explicitly includes pluricentric variants. This means Austrian and Swiss vocabulary (e.g. 'Jänner' instead of 'Januar') is fully accepted and often featured in the reading/listening sections.",
  },
  {
    question: 'What happens if I fail only one module of the ÖSD B2?',
    answer: 'Like the B1, the ÖSD B2 is modular. You can retake individual modules (Reading, Listening, Writing, or Speaking) as many times as needed to complete the full certificate.',
  },
];

function BlogFaqItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen
            ? 'border-black shadow-2xl scale-[1.02]'
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        aria-expanded={isOpen}
      >
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen
                ? 'bg-black text-white scale-110 rotate-6'
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              <HelpCircle className="w-6 h-6 relative z-10" />
            </div>
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>
          <div className="flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen ? 'rotate-180 text-black scale-125' : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'}
            `} />
          </div>
        </div>
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>
            <p className="text-black/70 leading-relaxed text-base md:text-lg max-w-none">
              {answer}
            </p>
          </div>
        </div>
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl pointer-events-none" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>
    </div>
  );
}

const OsdB2RedemittelPost = ({ pdfUrl }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        The ÖSD Zertifikat B2 is a critical benchmark for professionals seeking medical, legal, or high-level vocational recognition in Austria and Switzerland. Unlike the B1 level, the B2 exam demands &quot;argumentative depth&quot; and a clear distinction between formal and semi-formal registers.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Essential Redemittel for ÖSD B2: Formal & Semi-Formal Tasks
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        The <em>Redemittel ÖSD B2</em> strategy is divided into two high-stakes tasks. Task 1 usually involves a formal letter or email (e.g., a complaint or request for information), while Task 2 requires a descriptive/argumentative text based on a prompt. Mastering the linguistic &quot;bridge&quot; between these tasks is essential for a passing score.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        For Task 1 (Formal Correspondence), you must master professional openings like &quot;Bezug nehmend auf Ihr Schreiben vom...&quot; or &quot;Ich wende mich an Sie, um mich über (...) zu beschweren.&quot; For Task 2, argumentative connectors such as &quot;Ein entscheidender Vorteil besteht darin, dass...&quot; or &quot;Im Gegensatz dazu lässt sich anführen, dass...&quot; are mandatory to demonstrate B2-level coherence. Focusing on these high-yield phrases allows you to maintain professional flow without getting stuck on complex syntax like Subjunctive II or Passive voice. Transitioning to C1 becomes a natural progression once these B2 structures are internalized.
      </p>


      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Practising the Phrases Under Exam Conditions
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        A phrase list helps most when you can recall it under time pressure. Learn the phrases from the tables below
        or the free PDF, then use them in a timed answer. Logami is built by German teachers and has 6 complete ÖSD B2 mock exams
        in the official format, gives AI feedback on your written answers, and includes a Redemittel trainer that
        brings each phrase back just before you are likely to forget it. Your first mock exam is free.
      </p>

      {pdfUrl && (
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
          If you want the complete collection of ÖSD-specific B2 phrases, you can download the PDF for the full Redemittel with examples for formal and argumentative tasks.{' '}
          <a
            href={pdfUrl}
            download={pdfUrl.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
          >
            Download the full ÖSD B2 Redemittel PDF here
          </a>
          .
        </p>
      )}

      <RedemittelPhraseTables
        provider="osd"
        level="b2"
        examName="ÖSD B2"
        pdfUrl={pdfUrl}
      />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-12 mb-6">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {BLOG_FAQ_ITEMS.map((item, index) => (
          <BlogFaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openFaqIndex === index}
            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
          />
        ))}
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        <strong>Last Updated:</strong> October 7, 2026
      </p>
      <SprechenSamplesCallout provider="osd" level="b2" />
      <RelatedResources provider="osd" level="b2" variant="redemittel" />

    </article>
  );
};

export default OsdB2RedemittelPost;
